import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import BaseCommandDropdown, {
    Path,
    SelectionEvent,
    TriggerProps,
    useCommandDropdown,
} from "@reusables/BaseCommandDropdown";
import BaseBadge from "@reusables/BaseBadge";
import { Product } from "@/types/general";

/**
 * Where a reservation is made: a location, optionally narrowed to a section.
 * `null` stands for a global reservation ("Any location").
 */
export type ReservationLocation = {
    store_id: number;
    section_id: number | null;
};

/**
 * Identity of a reservation's location. All global reservations share the same key, which keeps a product
 * limited to a single global reservation and a single reservation per location / section.
 */
export function getLocationKey(location: ReservationLocation | null): string {
    if (location === null) return "global";

    return location.section_id === null
        ? `${location.store_id}`
        : `${location.store_id}_${location.section_id}`;
}

const GLOBAL_KEY = "global";
const WHOLE_LOCATION_KEY = "whole";

interface LocationNode {
    key: string | number;
    label: string;
    /** What selecting the node reserves at. Absent for locations that only group their sections. */
    location?: ReservationLocation | null;
    isDefault: boolean;
    children: LocationNode[];
}

// Stable getters, so the levels don't rebuild their rows on every render
const getNodeKey = (node: LocationNode) => node.key;
const getNodeLabel = (node: LocationNode) => node.label;
const getNodeChildren = (node: LocationNode) => node.children;
const hasNodeChildren = (node: LocationNode) => node.children.length > 0;

export interface ReservationLocationCommandDropdownProps {
    /** Locations and sections the product is stocked at */
    locations: Product.Components.Location[];
    value: ReservationLocation | null;
    onChange: (location: ReservationLocation | null) => void;
    /** Keys (see `getLocationKey`) already used by the product's other reservations */
    excludedKeys: Set<string>;
    /** While true, `locations` aren't known yet, so a location value can't be resolved */
    isLoading?: boolean;
    size?: TriggerProps["size"];
    /** Opens the picker right away (e.g. for a row that was just added) */
    autoOpen?: boolean;
    /** Called when a picker that was opened through `autoOpen` gets closed */
    onAutoOpenHandled?: () => void;
}

/**
 * Picks where a product is reserved: "Any location", a location, or one of its sections.
 * Locations with sections open as a separate level holding "Whole location" (when the product is stocked
 * at the location itself) and the sections; locations without sections are selected directly.
 * Searching from the top level also finds sections.
 */
export default function ReservationLocationCommandDropdown({
    locations,
    value,
    onChange,
    excludedKeys,
    isLoading = false,
    size,
    autoOpen = false,
    onAutoOpenHandled,
}: ReservationLocationCommandDropdownProps) {
    const { t } = useTranslation("", {
        keyPrefix: "general.components.reservationLocationCommandDropdown",
    });

    const [isOpen, setIsOpen] = useState(autoOpen);

    useEffect(() => {
        if (autoOpen) setIsOpen(true);
    }, [autoOpen]);

    const handleOpenChange = (open: boolean) => {
        setIsOpen(open);

        if (!open && autoOpen) {
            onAutoOpenHandled?.();
        }
    };

    const currentKey = getLocationKey(value);
    // Sets are recreated by callers on every render, so the tree depends on their content instead
    const excludedSignature = Array.from(excludedKeys).sort().join("|");

    const tree = useMemo(() => {
        const excluded = new Set(excludedSignature ? excludedSignature.split("|") : []);

        return buildTree({
            locations,
            // The current value stays selectable even if a caller lists it as used
            isAvailable: (location) => {
                const key = getLocationKey(location);
                return key === currentKey || !excluded.has(key);
            },
            anyLocationLabel: t("anyLocation"),
            wholeLocationLabel: t("wholeLocation"),
        });
    }, [locations, currentKey, excludedSignature, t]);

    const findNode = useCallback(
        (path: Path): LocationNode | undefined => {
            let nodes = tree;
            let node: LocationNode | undefined;

            for (const key of path) {
                node = nodes.find((candidate) => candidate.key === key);
                if (!node) return undefined;
                nodes = node.children;
            }

            return node;
        },
        [tree]
    );

    const valuePath = useMemo((): Path | null => {
        if (value === null) return [GLOBAL_KEY];

        // The product's locations aren't loaded yet, so the trigger shows its loading state instead of raw ids
        if (isLoading) return null;

        const locationNode = findNode([value.store_id]);

        // Inside a location with sections, the location itself is the "Whole location" entry
        const path: Path = locationNode?.children.length
            ? [value.store_id, value.section_id ?? WHOLE_LOCATION_KEY]
            : [value.store_id];

        if (!findNode(path)) {
            throw new Error(
                `Reservation location ${currentKey} is not among the locations the product is stocked at`
            );
        }

        return path;
    }, [value, isLoading, findNode, currentKey]);

    const describePath = useCallback(
        (path: Path): string => {
            const [locationKey, childKey] = path;
            const locationNode = findNode([locationKey]);

            if (!locationNode) {
                throw new Error(`Unknown reservation location path ${JSON.stringify(path)}`);
            }

            if (childKey === undefined || childKey === WHOLE_LOCATION_KEY) {
                return locationNode.label;
            }

            const sectionNode = findNode(path);

            if (!sectionNode) {
                throw new Error(`Unknown reservation location path ${JSON.stringify(path)}`);
            }

            return `${locationNode.label} - ${sectionNode.label}`;
        },
        [findNode]
    );

    const handleSelect = (event: SelectionEvent) => {
        // A reservation always has a location, so clicking the selected entry again keeps it
        if (event.action === "deselect" || !event.current) return;

        const node = findNode(event.current.path);

        if (!node || node.location === undefined) {
            throw new Error(
                `Selected path ${JSON.stringify(event.current.path)} is not a reservation location`
            );
        }

        onChange(node.location);
    };

    return (
        <BaseCommandDropdown
            filterMode="all-levels"
            value={valuePath}
            onSelect={handleSelect}
            isOpen={isOpen}
            onOpenChange={handleOpenChange}
        >
            <RegistryHydrator nodes={tree} />

            <BaseCommandDropdown.Trigger
                getLabel={(path) => describePath(path)}
                isLoading={isLoading}
                placeholder={t("heading")}
                size={size}
            />

            <BaseCommandDropdown.Modal title={t("heading")}>
                <BaseCommandDropdown.Search />

                <BaseCommandDropdown.Content maxHeight={600}>
                    <BaseCommandDropdown.Empty>{t("noResults")}</BaseCommandDropdown.Empty>
                    <LocationLevel nodes={tree} defaultBadgeLabel={t("defaultBadge")} />
                </BaseCommandDropdown.Content>
            </BaseCommandDropdown.Modal>
        </BaseCommandDropdown>
    );
}

/**
 * Every location a product can be reserved at, in the order the picker lists them ("Any location" first).
 */
export function listReservationLocations(
    locations: Product.Components.Location[]
): Array<ReservationLocation | null> {
    const collect = (nodes: LocationNode[]): Array<ReservationLocation | null> =>
        nodes.flatMap((node) => [
            ...(node.location !== undefined ? [node.location] : []),
            ...collect(node.children),
        ]);

    return collect(
        buildTree({
            locations,
            isAvailable: () => true,
            anyLocationLabel: "",
            wholeLocationLabel: "",
        })
    );
}

/**
 * Turns the product's flat location entries into "Any location" + locations, where locations with sections
 * hold "Whole location" (if stocked there) and their sections.
 *
 * Order: "Any location", the location holding the product's default entry, then locations A→Z;
 * inside a location "Whole location" first, then sections A→Z.
 */
function buildTree({
    locations,
    isAvailable,
    anyLocationLabel,
    wholeLocationLabel,
}: {
    locations: Product.Components.Location[];
    isAvailable: (location: ReservationLocation | null) => boolean;
    anyLocationLabel: string;
    wholeLocationLabel: string;
}): LocationNode[] {
    const groups = new Map<number, { name: string; entries: Product.Components.Location[] }>();

    locations.forEach((entry) => {
        const group = groups.get(entry.location_id) ?? { name: entry.location_name, entries: [] };
        group.entries.push(entry);
        groups.set(entry.location_id, group);
    });

    const locationNodes = Array.from(groups.entries()).flatMap(
        ([storeId, group]): LocationNode[] => {
            const wholeEntry = group.entries.find((entry) => !entry.sub_location_id);

            const sectionNodes = group.entries
                .flatMap((entry): LocationNode[] => {
                    if (!entry.sub_location_id) return [];

                    if (!entry.section_name) {
                        throw new Error(
                            `Section #${entry.sub_location_id} of location #${storeId} has no name`
                        );
                    }

                    return [
                        {
                            key: entry.sub_location_id,
                            label: entry.section_name,
                            location: { store_id: storeId, section_id: entry.sub_location_id },
                            isDefault: entry.is_default,
                            children: [],
                        },
                    ];
                })
                .sort((a, b) => a.label.localeCompare(b.label));

            const wholeLocation: ReservationLocation = { store_id: storeId, section_id: null };

            // Stocked at the location only: selected directly, without a level holding a single entry
            if (!sectionNodes.length) {
                if (!wholeEntry || !isAvailable(wholeLocation)) return [];

                return [
                    {
                        key: storeId,
                        label: group.name,
                        location: wholeLocation,
                        isDefault: wholeEntry.is_default,
                        children: [],
                    },
                ];
            }

            const children = [
                ...(wholeEntry
                    ? [
                          {
                              key: WHOLE_LOCATION_KEY,
                              label: wholeLocationLabel,
                              location: wholeLocation,
                              isDefault: wholeEntry.is_default,
                              children: [],
                          },
                      ]
                    : []),
                ...sectionNodes,
            ].filter((node) => node.location !== undefined && isAvailable(node.location));

            if (!children.length) return [];

            return [
                {
                    key: storeId,
                    label: group.name,
                    isDefault: group.entries.some((entry) => entry.is_default),
                    children,
                },
            ];
        }
    );

    locationNodes.sort(
        (a, b) => Number(b.isDefault) - Number(a.isDefault) || a.label.localeCompare(b.label)
    );

    const anyLocation: LocationNode = {
        key: GLOBAL_KEY,
        label: anyLocationLabel,
        location: null,
        isDefault: false,
        children: [],
    };

    return [...(isAvailable(null) ? [anyLocation] : []), ...locationNodes];
}

function LocationLevel({
    nodes,
    defaultBadgeLabel,
}: {
    nodes: LocationNode[];
    defaultBadgeLabel: string;
}) {
    return (
        <BaseCommandDropdown.Level
            options={nodes}
            getKey={getNodeKey}
            getLabel={getNodeLabel}
            getChildren={getNodeChildren}
            hasChildren={hasNodeChildren}
            renderItem={(node, { checkElement, chevronElement, ancestorLabels }) => (
                <div className="flex flex-row items-center justify-between w-full gap-2">
                    <div className="flex items-center gap-2 min-w-0 text-accent">
                        <span className="break-words">
                            {ancestorLabels.length > 0 && (
                                <span className="text-gray-500">
                                    {ancestorLabels.join(" › ")} ›{" "}
                                </span>
                            )}
                            {node.label}
                        </span>
                        {node.isDefault && (
                            <BaseBadge className="px-2 py-0.5">{defaultBadgeLabel}</BaseBadge>
                        )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                        {checkElement}
                        {chevronElement}
                    </div>
                </div>
            )}
        >
            {(parent) => (
                <LocationLevel nodes={parent.children} defaultBadgeLabel={defaultBadgeLabel} />
            )}
        </BaseCommandDropdown.Level>
    );
}

/**
 * Registers every node up front, so the trigger, breadcrumbs and selected chip have labels
 * before the corresponding level is opened.
 */
function RegistryHydrator({ nodes }: { nodes: LocationNode[] }) {
    const { registerLabel, registerItemForPath } = useCommandDropdown();

    useEffect(() => {
        const traverse = (items: LocationNode[], parentPath: Path) => {
            items.forEach((item) => {
                const path = [...parentPath, item.key];
                registerLabel(path, item.label);
                registerItemForPath(path, item);
                traverse(item.children, path);
            });
        };

        traverse(nodes, []);
    }, [nodes, registerLabel, registerItemForPath]);

    return null;
}
